import dayjs from 'dayjs';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { environment } from '@/config/environment';
import type { SchedulingItem } from '@/types';
import type { SortFilterOption, SortFilterValue } from '@/components/shared/SortFilterDialog';
import {
  FILTER_DEFINITIONS,
  MAX_EXTRACT_RECORDS,
  SCHEDULING_SORT_FIELDS,
  toIsoDate,
} from '@/services/scheduling/schedulingService';

export { MAX_EXTRACT_RECORDS, readDownloadError } from '@/services/scheduling/schedulingService';

export type SupplyPointTab = 'TRUCK' | 'RAIL' | 'MCT' | 'MARINE BULK';
export type SupplyPointPageType = 'loading' | 'deliveries';
export type SupplyPointItem = SchedulingItem;

export const SUPPLY_POINT_TABS: SupplyPointTab[] = ['TRUCK', 'RAIL', 'MCT', 'MARINE BULK'];
export const SUPPLY_POINT_PAGE_SIZE_OPTIONS = [25, 50, 100];

const MODES_OF_TRANSPORTATION: Record<SupplyPointTab, string[]> = {
  TRUCK: ['Truck'],
  RAIL: ['Rail'],
  MCT: ['MCT'],
  'MARINE BULK': ['MBL', 'Pipeline'],
};

// Page membership comes from the shipment status, not the order status.
const SHIPMENT_STATUSES: Record<SupplyPointPageType, string[]> = {
  loading: ['IM-Confirmed', 'MOT-Scheduled', 'MOT-Scheduled/updated'],
  deliveries: ['SP-Loaded', 'SP-Received'],
};

const DEFAULT_SORT_COLUMN: Record<SupplyPointPageType, string> = {
  loading: 'PlannedLoadDate',
  deliveries: 'RequestedDeliveryDate',
};

export const PAGE_TITLES: Record<SupplyPointPageType, string> = {
  loading: 'Supply Point - Loading',
  deliveries: 'Supply Point - Receiving',
};

export interface SupplyPointFormat {
  format: string;
  label: string;
  // Only offered on this tab; formats without a tab are offered on every tab.
  tab?: SupplyPointTab;
}

// Load Confirmation, the pricing templates, Global Freight Forwarder, RailTrack and Yard Master are supported.
export const EXPORT_FORMATS: SupplyPointFormat[] = [
  { format: 'SUPPLYPOINT_LOAD_CONFIRMATION', label: 'Load Confirmation' },
  { format: 'SUPPLYPOINT_NO_PRICING', label: 'Supply Point WITHOUT Pricing' },
  { format: 'SUPPLYPOINT_WITH_PRICING', label: 'Supply Point WITH Pricing' },
  { format: 'SUPPLYPOINT_GFF', label: 'Global Freight Forwarder' },
  { format: 'RAILTRACK', label: 'Rail - RailTrack', tab: 'RAIL' },
  { format: 'YARD MASTER', label: 'Rail - Yard Master', tab: 'RAIL' },
];

export const getExportFormats = (tab: SupplyPointTab): SupplyPointFormat[] =>
  EXPORT_FORMATS.filter((item) => !item.tab || item.tab === tab);

export interface SupplyPointImportFormat {
  format: string;
  label: string;
  tabs: SupplyPointTab[];
}

// Same templates as the legacy import dialog: Load Confirmation everywhere, Global Freight
// Forwarder only for the ocean tabs. Loading and Receiving offer the same formats.
export const IMPORT_FORMATS: SupplyPointImportFormat[] = [
  { format: 'SUPPLYPOINT_LOAD_CONFIRMATION', label: 'Load Confirmation', tabs: ['TRUCK', 'RAIL', 'MCT', 'MARINE BULK'] },
  { format: 'SUPPLYPOINT_GFF', label: 'Global Freight Forwarder', tabs: ['MCT', 'MARINE BULK'] },
];

export const getImportFormats = (tab: SupplyPointTab): SupplyPointImportFormat[] =>
  IMPORT_FORMATS.filter((item) => item.tabs.includes(tab));

// The download API rejects unknown filter columns, and OrderStatus is not valid for this format.
const FILTER_DEFINITIONS_BY_PROPERTY = new Map(
  FILTER_DEFINITIONS
    .filter((definition) => definition.column !== 'OrderStatus')
    .map((definition) => [definition.property, definition]),
);

const byLabel = (left: SortFilterOption, right: SortFilterOption) => left.label.localeCompare(right.label);

export const SUPPLY_POINT_FILTER_FIELDS: SortFilterOption[] = Array.from(FILTER_DEFINITIONS_BY_PROPERTY.values())
  .map(({ property, label }) => ({ property, label }))
  .sort(byLabel);

export const SUPPLY_POINT_SORT_FIELDS: SortFilterOption[] = SCHEDULING_SORT_FIELDS
  .filter((field) => field.property !== 'OrderStatus');

export const getModesOfTransportation = (tab: SupplyPointTab) => MODES_OF_TRANSPORTATION[tab];

export const getShipmentStatuses = (pageType: SupplyPointPageType) => SHIPMENT_STATUSES[pageType];

export const parsePageType = (value: string | null): SupplyPointPageType => (
  value === 'deliveries' ? 'deliveries' : 'loading'
);

const buildSorting = (pageType: SupplyPointPageType, sorts: SortFilterValue[]) => (
  sorts.length > 0
    ? sorts.map((sort) => ({ ColumnName: sort.property, Value: sort.value }))
    : [
      { ColumnName: DEFAULT_SORT_COLUMN[pageType], Value: 'ASC' },
      { ColumnName: 'OrderNumber', Value: 'ASC' },
      { ColumnName: 'ItemNumber', Value: 'ASC' },
    ]
);

export interface SupplyPointListInput {
  tab: SupplyPointTab;
  pageType: SupplyPointPageType;
  page: number;
  pageSize: number;
  sorts: SortFilterValue[];
  filters: SortFilterValue[];
}

export interface SupplyPointListRequest {
  params: Record<string, unknown>;
  ignoredFilters: SortFilterValue[];
}

export const buildSupplyPointListRequest = ({
  tab,
  pageType,
  page,
  pageSize,
  sorts,
  filters,
}: SupplyPointListInput): SupplyPointListRequest => {
  // The list endpoint binds scalar values, so multi-value filters are sent comma-joined.
  const params: Record<string, unknown> = {
    PageNumber: page,
    PageSize: pageSize,
    ModeOfTransportation: getModesOfTransportation(tab).join(','),
    ModeOfTransportationFilter: 'in',
    ShipmentStatus: getShipmentStatuses(pageType).join(','),
    ShipmentStatusFilter: 'in',
    Sorting: buildSorting(pageType, sorts),
  };
  const ignoredFilters: SortFilterValue[] = [];

  filters.forEach((filter) => {
    const definition = FILTER_DEFINITIONS_BY_PROPERTY.get(filter.property);
    const value = definition?.isDate ? toIsoDate(filter.value) : filter.value.trim();

    if (!definition || !value || definition.column in params) {
      ignoredFilters.push(filter);
      return;
    }

    params[definition.column] = value;
    params[`${definition.column}Filter`] = definition.operator;
  });

  return { params, ignoredFilters };
};

export interface SupplyPointListResult {
  items: SupplyPointItem[];
  total: number;
}

export const fetchSupplyPoint = async (params: Record<string, unknown>): Promise<SupplyPointListResult> => {
  const { data } = await axiosInstance.get('/shipment_supplypoint', { params, headers: jsonHeaders() });
  const items = (data?.datas ?? data?.data ?? []) as SupplyPointItem[];

  return { items, total: Number(data?.totalCount ?? data?.count ?? items.length) };
};

export interface DownloadSupplyPointInput {
  tab: SupplyPointTab;
  pageType: SupplyPointPageType;
  format: string;
  source: string;
  userId: string;
  filters: SortFilterValue[];
}

// Mirrors the legacy export body: the page statuses go in OrderStatus (CyberOrderStatus) and there is no Sorting.
export const buildSupplyPointDownloadRequest = ({
  tab,
  pageType,
  format,
  source,
  userId,
  filters,
}: DownloadSupplyPointInput) => {
  const columnFilters = filters.flatMap((filter) => {
    const definition = FILTER_DEFINITIONS_BY_PROPERTY.get(filter.property);
    const value = definition?.isDate ? toIsoDate(filter.value) : filter.value.trim();

    return definition && value ? [{ ColumnName: filter.property, Value: value }] : [];
  });

  return {
    UserId: userId,
    ProductType: null,
    Source: source,
    RecordId: null,
    SearchFilters: '',
    FormatType: format,
    ModeOfTransportation: getModesOfTransportation(tab),
    OrderStatus: getShipmentStatuses(pageType),
    LinesPerPage: MAX_EXTRACT_RECORDS,
    PageNumber: 1,
    Filters: columnFilters,
    Permission: true,
  };
};

export const downloadSupplyPoint = async (input: DownloadSupplyPointInput) => {
  const { data } = await axiosInstance.post(
    '/download',
    buildSupplyPointDownloadRequest(input),
    { headers: jsonHeaders(), responseType: 'blob', timeout: environment.downloadTimeout },
  );
  const tab = input.tab.toLowerCase().replace(/\s+/g, '_');

  return {
    blob: data instanceof Blob ? data : new Blob([data]),
    filename: `supplypoint_${input.pageType}_${tab}_${input.format.toLowerCase()}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
  };
};
